import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { AccountProfile, SellerProfile, type SwitchableAccountProfile } from '@enums/accountProfile.ts';
import type { User } from '@models/auth/user';
import { SELLER_MANAGEMENT } from '@pages/inbox/conversations-show/ProfileEnum';

export const useUserProfile = defineStore('userProfile', () => {
  const currentProfile = ref<SwitchableAccountProfile | null>(null);

  const user = computed<User | null>(() => {
    if (!window.USER) {
      return null;
    }

    return window.USER as User;
  });

  const userProfile = computed<AccountProfile>(() => {
    if (!user.value) {
      return AccountProfile.ANONYMOUS;
    }

    const {
      seller_profiles: sellerProfiles,
      buyer,
    } = user.value.organization;

    const hasSellerProfiles = sellerProfiles?.length > 0;

    if (!buyer) {
      return hasSellerProfiles ? AccountProfile.SELLER : AccountProfile.UNQUALIFIED;
    }

    return hasSellerProfiles ? AccountProfile.BOTH : AccountProfile.BUYER;
  });

  const isSellerBoost = computed<boolean>(() => {
    if (!user.value || userProfile.value === AccountProfile.BUYER) {
      return false;
    }

    const { seller_profiles: sellerProfiles } = user.value.organization;

    return Array.isArray(sellerProfiles)
      && (sellerProfiles.includes(SellerProfile.SELLER_BOOST) || sellerProfiles.includes(SellerProfile.SELLER_BOOST));
  });

  const hasKYCActive = computed<boolean>(() => {
    if (!user.value?.organization.pay_organization_seller) {
      return false;
    }

    return user.value.organization.pay_organization_seller.has_kyc_active;
  });

  const hasPayBuyer = computed<boolean>(() => {
    if (!user.value?.organization) {
      return false;
    }

    const subscriptions = user.value.organization.pay_organization_buyer?.subscriptions;

    // Only show buyer payment method if organization is linked to a PayOrganizationBuyer entity and has at least one subscription
    return !!subscriptions && subscriptions.length > 0;
  });

  const isBuyer = computed<boolean>(() => !!user.value?.organization?.buyer);

  const isProfileManagement = computed<boolean>(() => {
    if (!user.value) {
      return false;
    }

    return user.value.organization.seller_profiles.includes(SELLER_MANAGEMENT);
  });

  const persistCurrentProfile = (preference : SwitchableAccountProfile): void => {
    localStorage.setItem('menuPreference', preference);
  };

  const switchCurrentProfile = (): void => {
    if (userProfile.value !== AccountProfile.BOTH) {
      return;
    }

    currentProfile.value = currentProfile.value === AccountProfile.SELLER ? AccountProfile.BUYER : AccountProfile.SELLER;

    persistCurrentProfile(currentProfile.value);
  };

  const initCurrentProfile = (): void => {
    if (userProfile.value === AccountProfile.BUYER) {
      currentProfile.value = AccountProfile.BUYER;
      return;
    }

    if (userProfile.value === AccountProfile.SELLER) {
      currentProfile.value = AccountProfile.SELLER;
      return;
    }

    const menuPreference = localStorage.getItem('menuPreference');

    if (userProfile.value === AccountProfile.BOTH) {
      currentProfile.value = menuPreference ? menuPreference as SwitchableAccountProfile : AccountProfile.SELLER;
      return;
    }

    currentProfile.value = null;
  };

  watch(userProfile, () => initCurrentProfile(), { immediate: true });

  return {
    user,
    userProfile,
    isSellerBoost,
    hasKYCActive,
    hasPayBuyer,
    isBuyer,
    currentProfile,
    switchCurrentProfile,
    isProfileManagement,
  };
});
